import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Chip,
  Divider,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Category as CategoryIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { message } from 'antd';
import { useSelector } from 'react-redux';
import axios from 'axios';

const UserDefinition = () => {
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  // State'ler
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state'leri
  const [formData, setFormData] = useState({
    id: 0,
    name: '',
    email: '',
    password: '',
    categories: [],
    isActive: true
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  
  // Arama ve filtreleme state'leri
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  useEffect(() => {
    if (!userData || !userData.id) {
      setError('Kullanıcı bilgileri eksik. Lütfen tekrar giriş yapın.');
      return;
    }
    
    fetchUsers();
    fetchCategories();
  }, [userData]);

  // Kullanıcıları getir
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://app.hhoms.com/api/form/FormUsers/list/all', {
        headers: {
          ApplicationToken: appToken,
          User: userData.id
        }
      });
      
      if (response.data && response.data.formUsers) {
        setUsers(response.data.formUsers);
      } else {
        setUsers([]);
      }
      
      setError(null);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
      setError('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Kategorileri getir
  const fetchCategories = async () => {
    try {
      const response = await axios.get('https://app.hhoms.com/api/form/Categories/list/all', {
        headers: {
          ApplicationToken: appToken,
          User: userData.id
        }
      });
      
      if (response.data && response.data.formCategories) {
        setCategories(response.data.formCategories);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
    }
  };

  // Form validasyonu
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Ad soyad gereklidir';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'E-posta gereklidir';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'Şifre gereklidir';
    } else if (formData.password && formData.password.length > 0 && formData.password.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    }

    if (!isEditing && formData.password !== confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    if (!formData.categories || formData.categories.length === 0) {
      newErrors.categories = 'En az bir kategori seçilmelidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form değişikliklerini handle et
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Yeni kullanıcı ekleme
  const handleAddUser = () => {
    setIsEditing(false);
    setFormData({
      id: 0,
      name: '',
      email: '',
      password: '',
      categories: [],
      isActive: true // Varsayılan olarak aktif
    });
    setConfirmPassword('');
    setErrors({});
    setDialogOpen(true);
  };

  // Kullanıcı düzenleme
  const handleEditUser = async (user) => {
    setIsEditing(true);
    setLoading(true);
    
    try {
      const response = await axios.get(`https://app.hhoms.com/api/form/FormUsers/${user.id}`, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id
        }
      });
      
      const userDetails = response.data.formUsers[0];
      
      // Kategorileri string'den array'e çevir
      let categoriesArray = [];
      if (userDetails.categories) {
        categoriesArray = userDetails.categories.split(',').map(cat => parseInt(cat.trim())).filter(cat => !isNaN(cat));
      }
      
      // Password'ü Base-64 decode et
   
      
      setFormData({
        id: userDetails.id,
        name: userDetails.name || '',
        email: userDetails.email || '',
        password: userDetails.password,
        categories: categoriesArray,
        isActive: userDetails.isActive !== undefined ? userDetails.isActive : true
      });
      
      setConfirmPassword('');
      setErrors({});
      setDialogOpen(true);
    } catch (error) {
      console.error('Kullanıcı bilgileri alınırken hata:', error);
      setError('Kullanıcı bilgileri alınırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcı silme
  const handleDeleteUser = async (userId) => {
    const loadingMessage = message.loading('Kullanıcı siliniyor...', 0);

    try {
      await axios.delete(`https://app.hhoms.com/api/form/FormUsers/${userId}`, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id
        }
      });
      
      loadingMessage();
      message.success('Kullanıcı başarıyla silindi!', 3);
      fetchUsers();
    } catch (error) {
      loadingMessage();
      console.error('Kullanıcı silinirken hata:', error);
      
      let errorMessage = 'Kullanıcı silinirken bir hata oluştu';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || error.response.data || errorMessage;
      }
      
      message.error(errorMessage, 4);
    }
  };

  // Kullanıcı durumunu değiştir
  const handleToggleStatus = async (user) => {
    const loadingMessage = message.loading(`${user.name} kullanıcısının durumu değiştiriliyor...`, 0);

    try {
      const newStatus = !user.isActive;
      
      await axios.put(`https://app.hhoms.com/api/form/FormUsers/statu/${user.id}/${newStatus}`, {}, {
        headers: {
          ApplicationToken: appToken,
          User: userData.id,
          'Content-Type': 'application/json'
        }
      });

      loadingMessage();
      message.success(`${user.name} kullanıcısı başarıyla ${newStatus ? 'aktif' : 'pasif'} yapıldı!`, 3);
      fetchUsers(); // Listeyi yenile
    } catch (error) {
      loadingMessage();
      console.error('Durum değiştirme hatası:', error);
      
      let errorMessage = 'Durum değiştirilirken bir hata oluştu';
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || error.response.data || errorMessage;
      }
      
      message.error(errorMessage, 4);
    }
  };

  // Form gönderme
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      message.warning('Lütfen tüm gerekli alanları doldurun!', 3);
      return;
    }

    setLoading(true);
    const loadingMessage = message.loading(
      isEditing ? 'Kullanıcı güncelleniyor...' : 'Kullanıcı ekleniyor...', 
      0
    );

    try {
      const submitData = {
        id: isEditing ? formData.id : 0,
        name: formData.name,
        email: formData.email,
        password: formData.password,
        categories: Array.isArray(formData.categories) ? formData.categories.join(',') : formData.categories,
        isActive: !!formData.isActive // boolean olarak gönder
      };
      console.log('Gönderilecek veri:', submitData);
      console.log('userData:', userData);
      console.log('formData:', formData);

      const response = isEditing
        ? await axios.put('https://app.hhoms.com/api/form/FormUsers', submitData, {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
              'Content-Type': 'application/json'
            }
          })
        : await axios.post('https://app.hhoms.com/api/form/FormUsers', submitData, {
            headers: {
              ApplicationToken: appToken,
              User: userData.id,
              'Content-Type': 'application/json'
            }
          });
      
      console.log('API yanıtı:', response.data);
      loadingMessage();
      message.success(
        isEditing ? 'Kullanıcı başarıyla güncellendi!' : 'Kullanıcı başarıyla eklendi!', 
        3
      );
      
      setDialogOpen(false);
      fetchUsers();
      setFormData({
        id: 0,
        name: '',
        email: '',
        password: '',
        categories: [],
        isActive: true
      });
      setConfirmPassword('');
    } catch (error) {
      loadingMessage();
      console.error('Kullanıcı kaydedilirken hata:', error);
      
      let errorMessage = 'Bilinmeyen hata';
      if (error.response) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data && typeof error.response.data === 'object') {
          if (error.response.data.errors) {
            const validationErrors = Object.entries(error.response.data.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('; ');
            errorMessage = `Validasyon hataları: ${validationErrors}`;
          } else {
            errorMessage = error.response.data.message || error.response.data.title || JSON.stringify(error.response.data);
          }
        } else {
          errorMessage = error.response.statusText || `HTTP ${error.response.status}`;
        }
        message.error(`Sunucu hatası: ${error.response.status} - ${errorMessage}`, 5);
      } else if (error.request) {
        message.error('Sunucuya bağlanılamıyor. Lütfen internet bağlantınızı kontrol edin.', 4);
      } else {
        message.error(`Bağlantı hatası: ${error.message}`, 4);
      }
    } finally {
      setLoading(false);
    }
  };

  // Tüm kategorileri seç
  const handleSelectAllCategories = () => {
    const allCategoryIds = categories.map(cat => cat.id);
    handleInputChange('categories', allCategoryIds);
  };

  // Tüm kategori seçimlerini temizle
  const handleClearCategories = () => {
    handleInputChange('categories', []);
  };

  // Tekil kategori silme
  const handleRemoveCategory = (categoryId) => {
    const updatedCategories = formData.categories.filter(id => id !== categoryId);
    handleInputChange('categories', updatedCategories);
  };

  // Dialog kapatma
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      id: 0,
      name: '',
      email: '',
      password: '',
      categories: [],
      isActive: true
    });
    setConfirmPassword('');
    setErrors({});
    setCategorySearchTerm(''); // Arama terimini temizle
  };

  // Kategorileri filtrele
  const getFilteredCategories = () => {
    if (!categorySearchTerm.trim()) {
      return categories;
    }
    return categories.filter(category =>
      category.title.toLowerCase().includes(categorySearchTerm.toLowerCase())
    );
  };

  // Arama terimini vurgula
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <Box
          key={index}
          component="span"
          sx={{
            backgroundColor: '#ffeb3b',
            color: '#000',
            fontWeight: 600,
            padding: '1px 2px',
            borderRadius: '2px'
          }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    );
  };

  // Arama istatistikleri
  const getSearchStats = () => {
    const filtered = getFilteredCategories();
    const selected = filtered.filter(cat => formData.categories.includes(cat.id));
    return {
      total: categories.length,
      filtered: filtered.length,
      selected: selected.length
    };
  };

  // Kategori isimlerini al
  const getCategoryNames = (userCategories) => {
    if (!userCategories) return 'Belirtilmemiş';
    
    // API'den gelen string formatını işle
    const categoryIds = typeof userCategories === 'string' 
      ? userCategories.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
      : Array.isArray(userCategories) ? userCategories : [];
    
    const categoryTitles = categories
      .filter(cat => categoryIds.includes(cat.id))
      .map(cat => cat.title);
    
    return categoryTitles.length > 0 ? categoryTitles.join(', ') : 'Belirtilmemiş';
  };

  // Kullanıcıları filtrele
  const getFilteredUsers = () => {
    let filteredUsers = users;

    // Arama filtresi
    if (searchTerm.trim()) {
      filteredUsers = filteredUsers.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getCategoryNames(user.categories).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Durum filtresi
    if (statusFilter === 'active') {
      filteredUsers = filteredUsers.filter(user => user.isActive !== false);
    } else if (statusFilter === 'inactive') {
      filteredUsers = filteredUsers.filter(user => user.isActive === false);
    }

    return filteredUsers;
  };

  // Arama terimini vurgula (kullanıcı listesi için)
  const highlightUserSearchTerm = (text, searchTerm) => {
    if (!searchTerm.trim()) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <Box
          key={index}
          component="span"
          sx={{
            backgroundColor: '#667eea',
            color: 'white',
            fontWeight: 600,
            padding: '2px 4px',
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    );
  };

  return (
    <Box sx={{ 
      p: 3, 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: '100vh'
    }}>
      {/* Hata ve başarı mesajları */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Üst toolbar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        p: 3,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)',
        color: 'white'
      }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Kullanıcı Yönetimi
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Toplam {users.length} kullanıcı • {users.filter(u => u.isActive !== false).length} aktif • {getFilteredUsers().length} gösteriliyor
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
          sx={{ 
            borderRadius: 3,
            backgroundColor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1.5,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.3)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Yeni Kullanıcı Ekle
        </Button>
      </Box>

      {/* Arama ve Filtre Alanları */}
      <Box sx={{ 
        mb: 4,
        p: 3,
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.08)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ fontSize: 24, mr: 1, color: '#667eea' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#4a5568' }}>
            Arama ve Filtreler
          </Typography>
        </Box>
        
        <Grid container spacing={3} alignItems="center">
          {/* Arama Kutusu */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Kullanıcı ara (ad, email, kategori)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#667eea', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ 
                        color: '#9e9e9e',
                        '&:hover': { 
                          color: '#d32f2f',
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: searchTerm ? '#e3f2fd' : '#f8f9fa',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: searchTerm ? '#bbdefb' : '#e9ecef'
                  },
                  '&.Mui-focused': {
                    backgroundColor: '#e3f2fd',
                    boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                  }
                }
              }}
            />
          </Grid>

          {/* Durum Filtresi */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Durum Filtresi</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Durum Filtresi"
                sx={{
                  borderRadius: 3,
                  backgroundColor: statusFilter !== 'all' ? '#e3f2fd' : '#f8f9fa',
                  '&:hover': {
                    backgroundColor: statusFilter !== 'all' ? '#bbdefb' : '#e9ecef'
                  }
                }}
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ fontSize: 18, mr: 1, color: '#667eea' }} />
                    Tümü
                  </Box>
                </MenuItem>
                <MenuItem value="active">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#00c851', 
                      mr: 1.5 
                    }} />
                    Aktif Kullanıcılar
                  </Box>
                </MenuItem>
                <MenuItem value="inactive">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: '#ff6b6b', 
                      mr: 1.5 
                    }} />
                    Pasif Kullanıcılar
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Temizle Butonu */}
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              disabled={!searchTerm && statusFilter === 'all'}
              sx={{
                borderRadius: 3,
                py: 1.5,
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#5a67d8',
                  backgroundColor: '#e3f2fd'
                },
                '&:disabled': {
                  borderColor: '#e2e8f0',
                  color: '#a0aec0'
                }
              }}
            >
              Filtreleri Temizle
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Kullanıcı listesi */}
      <Grid container spacing={3}>
        {loading ? (
          <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : getFilteredUsers().length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ 
              textAlign: 'center',
              py: 8,
              px: 4
            }}>
              <Box sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: searchTerm || statusFilter !== 'all' 
                  ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3,
                boxShadow: searchTerm || statusFilter !== 'all' 
                  ? '0 8px 32px rgba(255, 107, 107, 0.3)'
                  : '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                {searchTerm || statusFilter !== 'all' ? (
                  <SearchIcon sx={{ fontSize: 48, color: 'white' }} />
                ) : (
                  <PersonIcon sx={{ fontSize: 48, color: 'white' }} />
                )}
              </Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#4a5568',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                {searchTerm || statusFilter !== 'all' 
                  ? 'Arama kriterlerinize uygun kullanıcı bulunamadı'
                  : 'Henüz kullanıcı bulunmuyor'
                }
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#718096',
                  mb: 4,
                  maxWidth: 400,
                  margin: '0 auto'
                }}
              >
                {searchTerm || statusFilter !== 'all' 
                  ? 'Lütfen arama terimlerinizi değiştirin veya filtreleri temizleyin'
                  : 'İlk kullanıcıyı ekleyerek başlayın ve sistem yönetimine başlayın'
                }
              </Typography>
              {searchTerm || statusFilter !== 'all' ? (
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                  }}
                  sx={{ 
                    borderRadius: 3,
                    borderColor: '#667eea',
                    color: '#667eea',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#5a67d8',
                      backgroundColor: '#e3f2fd'
                    }
                  }}
                >
                  Filtreleri Temizle
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                  size="large"
                  sx={{ 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  İlk Kullanıcıyı Ekle
                </Button>
              )}
            </Box>
          </Grid>
        ) : (
          getFilteredUsers().map((user) => (
            <Grid item xs={12} md={6} lg={4} key={user.id}>
              <Card sx={{ 
                height: '100%',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  '& .user-avatar': {
                    transform: 'scale(1.1)',
                  },
                  '& .action-buttons': {
                    opacity: 1,
                    transform: 'translateY(0)'
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: user.isActive !== false 
                    ? 'linear-gradient(90deg, #00c851 0%, #007E33 100%)'
                    : 'linear-gradient(90deg, #ff6b6b 0%, #ee5a24 100%)',
                }
              }}>
                <CardContent sx={{ p: 3, position: 'relative' }}>
                  {/* Status Badge */}
                  <Box sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 2
                  }}>
                    <Chip
                      label={user.isActive !== false ? 'Aktif' : 'Pasif'}
                      onClick={() => handleToggleStatus(user)}
                      size="small"
                      sx={{ 
                        background: user.isActive !== false 
                          ? 'linear-gradient(135deg, #00c851 0%, #007E33 100%)'
                          : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        border: 'none',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    />
                  </Box>

                  {/* User Info */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                    <Avatar 
                      className="user-avatar"
                      sx={{ 
                        width: 60,
                        height: 60,
                        mr: 2,
                        background: user.isActive !== false 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #bbb 0%, #999 100%)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        transition: 'all 0.3s ease',
                        border: '3px solid white'
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, pt: 0.5 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#2d3748',
                          mb: 0.5,
                          fontSize: '1.1rem'
                        }}
                      >
                        {highlightUserSearchTerm(user.name, searchTerm)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ fontSize: 16, mr: 1, color: '#667eea' }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#718096',
                            fontSize: '0.875rem'
                          }}
                        >
                          {highlightUserSearchTerm(user.email, searchTerm)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Categories */}
                  <Box sx={{ 
                    mb: 3,
                    p: 2,
                    backgroundColor: 'rgba(102, 126, 234, 0.05)',
                    borderRadius: 2,
                    border: '1px solid rgba(102, 126, 234, 0.1)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CategoryIcon sx={{ fontSize: 18, mr: 1, color: '#667eea' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#4a5568' }}>
                        Kategoriler
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#718096',
                        lineHeight: 1.5,
                        fontSize: '0.875rem'
                      }}
                    >
                      {highlightUserSearchTerm(getCategoryNames(user.categories), searchTerm)}
                    </Typography>
                  </Box>
                  
                  {/* Action Buttons */}
                  <Box 
                    className="action-buttons"
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      gap: 1,
                      opacity: 0.7,
                      transform: 'translateY(4px)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <IconButton
                      size="medium"
                      onClick={() => handleEditUser(user)}
                      disabled={loading}
                      sx={{ 
                        backgroundColor: '#667eea',
                        color: 'white',
                        width: 44,
                        height: 44,
                        '&:hover': { 
                          backgroundColor: '#5a67d8',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {loading ? <CircularProgress size={18} color="inherit" /> : <EditIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="medium"
                      onClick={() => handleDeleteUser(user.id)}
                      sx={{ 
                        backgroundColor: '#ff6b6b',
                        color: 'white',
                        width: 44,
                        height: 44,
                        '&:hover': { 
                          backgroundColor: '#ee5a24',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Kullanıcı ekleme/düzenleme dialog'u */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f8f9fa',
          borderBottom: '1px solid #e9ecef',
          fontWeight: 600
        }}>
          {isEditing ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ad Soyad"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-posta"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Şifre"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password || (isEditing ? 'Mevcut şifre görünüyor, değiştirmek istiyorsanız yeni şifre girin' : 'Şifre en az 6 karakter olmalıdır')}
                  placeholder={isEditing ? 'Mevcut şifre veya yeni şifre girin' : 'Şifre girin'}
                  required={!isEditing}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: '#6c757d' }}
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {!isEditing && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Şifre Tekrar"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                            sx={{ color: '#6c757d' }}
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.categories}>
                  <InputLabel>Kategoriler</InputLabel>
                  <Select
                    multiple
                    value={formData.categories}
                    onChange={(e) => handleInputChange('categories', e.target.value)}
                    label="Kategoriler"
                    renderValue={(selected) => (
                      <Box 
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 0.5 }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {selected.map((value) => {
                          const category = categories.find(cat => cat.id === value);
                          return (
                            <Chip
                              key={`chip-${value}`}
                              label={category?.title || `ID: ${value}`}
                              size="small"
                              variant="filled"
                              color="primary"
                              onDelete={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setTimeout(() => {
                                  handleRemoveCategory(value);
                                }, 0);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              deleteIcon={<CloseIcon fontSize="small" />}
                              sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                fontSize: '0.75rem',
                                '& .MuiChip-deleteIcon': {
                                  color: '#1976d2',
                                  fontSize: '16px',
                                  '&:hover': {
                                    color: '#d32f2f',
                                    backgroundColor: 'rgba(211, 47, 47, 0.1)'
                                  }
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    sx={{
                      borderRadius: 2,
                      '& .MuiSelect-select': {
                        minHeight: formData.categories.length > 0 ? '80px' : 'auto',
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingTop: '8px'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 400,
                          '& .MuiMenuItem-root': {
                            borderRadius: 1,
                            margin: '2px 8px',
                            '&:last-child': {
                              marginBottom: '8px'
                            }
                          },
                          // Pulse animasyonu için keyframes
                          '@keyframes pulse': {
                            '0%': {
                              opacity: 1,
                            },
                            '50%': {
                              opacity: 0.5,
                            },
                            '100%': {
                              opacity: 1,
                            },
                          }
                        }
                      }
                    }}
                  >
                    {/* Arama kutusu */}
                    <Box sx={{ 
                      p: 2, 
                      borderBottom: '1px solid #e0e0e0',
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'white',
                      zIndex: 1000
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    >
                      <TextField
                        size="small"
                        placeholder="Kategori ara..."
                        value={categorySearchTerm}
                        onChange={(e) => {
                          e.stopPropagation();
                          setCategorySearchTerm(e.target.value);
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          endAdornment: categorySearchTerm && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setCategorySearchTerm('');
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                sx={{ 
                                  color: '#9e9e9e',
                                  '&:hover': { 
                                    color: '#d32f2f',
                                    backgroundColor: '#ffebee'
                                  }
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: categorySearchTerm ? '#e3f2fd' : '#f8f9fa',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: categorySearchTerm ? '#bbdefb' : '#e9ecef'
                            },
                            '&.Mui-focused': {
                              backgroundColor: '#e3f2fd'
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: '#000',
                            fontSize: '0.875rem'
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        onFocus={(e) => {
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                        }}
                        autoComplete="off"
                      />
                      
                      {/* Arama istatistikleri */}
                      {categorySearchTerm.trim() && (
                        <Box sx={{ 
                          mt: 1, 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {(() => {
                              const stats = getSearchStats();
                              return `${stats.filtered} sonuç bulundu (${stats.selected} seçili)`;
                            })()}
                          </Typography>
                          {getFilteredCategories().length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1 }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            >
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const filteredIds = getFilteredCategories().map(cat => cat.id);
                                  const newSelected = [...new Set([...formData.categories, ...filteredIds])];
                                  handleInputChange('categories', newSelected);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                  fontSize: '0.7rem',
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  color: '#1976d2',
                                  '&:hover': { backgroundColor: '#e3f2fd' }
                                }}
                              >
                                Tümünü Seç
                              </Button>
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const filteredIds = getFilteredCategories().map(cat => cat.id);
                                  const newSelected = formData.categories.filter(id => !filteredIds.includes(id));
                                  handleInputChange('categories', newSelected);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                sx={{
                                  fontSize: '0.7rem',
                                  minWidth: 'auto',
                                  px: 1,
                                  py: 0.25,
                                  color: '#d32f2f',
                                  '&:hover': { backgroundColor: '#ffebee' }
                                }}
                              >
                                Seçimi Kaldır
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {getFilteredCategories().length === 0 ? (
                      <Box sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        borderRadius: 2,
                        backgroundColor: '#f8f9fa',
                        margin: '8px'
                      }}>
                        <SearchIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {categorySearchTerm.trim() 
                            ? `"${categorySearchTerm}" için sonuç bulunamadı` 
                            : 'Kategori bulunamadı'
                          }
                        </Typography>
                        {categorySearchTerm.trim() && (
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setCategorySearchTerm('');
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();  
                              e.stopPropagation();
                            }}
                            sx={{ 
                              mt: 1,
                              color: '#1976d2',
                              fontSize: '0.75rem'
                            }}
                          >
                            Aramayı temizle
                          </Button>
                        )}
                      </Box>
                    ) : (
                      getFilteredCategories().map((category) => (
                        <MenuItem 
                          key={category.id} 
                          value={category.id}
                          sx={{
                            backgroundColor: formData.categories.includes(category.id) ? '#e3f2fd' : 'transparent',
                            '&:hover': {
                              backgroundColor: formData.categories.includes(category.id) ? '#bbdefb' : '#f5f5f5',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease'
                            },
                            transition: 'all 0.2s ease',
                            borderLeft: formData.categories.includes(category.id) ? '3px solid #1976d2' : '3px solid transparent',
                            '&.Mui-selected': {
                              backgroundColor: '#e3f2fd !important'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 0.5 }}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                border: '2px solid #1976d2',
                                borderRadius: '3px',
                                backgroundColor: formData.categories.includes(category.id) ? '#1976d2' : 'transparent',
                                mr: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              {formData.categories.includes(category.id) && (
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    backgroundColor: 'white',
                                    borderRadius: '1px'
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: formData.categories.includes(category.id) ? 600 : 400,
                                  color: formData.categories.includes(category.id) ? '#1976d2' : 'inherit',
                                  lineHeight: 1.4
                                }}
                              >
                                {highlightSearchTerm(category.title, categorySearchTerm)}
                              </Typography>
                            </Box>
                            {formData.categories.includes(category.id) && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: '#4caf50',
                                  ml: 1,
                                  animation: 'pulse 1.5s infinite'
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.categories && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.categories}
                    </Typography>
                  )}
                  
                  {/* Kategori yönetim butonları */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      size="small"
                      startIcon={<SelectAllIcon />}
                      onClick={handleSelectAllCategories}
                      disabled={formData.categories.length === categories.length}
                      sx={{
                        color: '#1976d2',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#e3f2fd'
                        }
                      }}
                    >
                      Tümünü Seç ({categories.length})
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={handleClearCategories}
                      disabled={formData.categories.length === 0}
                      sx={{
                        color: '#d32f2f',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#ffebee'
                        }
                      }}
                    >
                      Tümünü Temizle
                    </Button>
                    {categorySearchTerm.trim() && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCategorySearchTerm('');
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                          color: '#9e9e9e',
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          minWidth: 'auto',
                          px: 1,
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            color: '#d32f2f'
                          }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        Aramayı Temizle
                      </Button>
                    )}
                    {formData.categories.length > 0 && (
                      <Typography variant="caption" sx={{ 
                        alignSelf: 'center', 
                        color: '#666',
                        ml: 'auto'
                      }}>
                        {formData.categories.length} / {categories.length} seçili
                      </Typography>
                    )}
                  </Box>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.value)}
                    label="Durum"
                    sx={{
                      borderRadius: 2
                    }}
                  >
                    <MenuItem value={true}>Aktif</MenuItem>
                    <MenuItem value={false}>Pasif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ 
                color: '#6c757d',
                '&:hover': { backgroundColor: '#e9ecef' }
              }}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{ 
                backgroundColor: '#3498db',
                '&:hover': {
                  backgroundColor: '#2980b9'
                },
                '&:disabled': {
                  backgroundColor: '#bdc3c7'
                }
              }}
            >
              {isEditing ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UserDefinition; 